// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const AuthContext = createContext(null);

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.asoserconvif.com';
const TOKEN_KEY = import.meta.env.VITE_TOKEN_KEY || 'scapp_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Leer token al iniciar
  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.expires_at && Date.now() > parsed.expires_at) {
          localStorage.removeItem(TOKEN_KEY);
        } else {
          setTokenInfo(parsed);
          setUser(parsed.user || null);
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    setLoading(false);
  }, []);

  // Llamada al backend Laravel
  const login = async ({ usuario, clave }) => {
    const { data } = await axios.post(`${API_BASE_URL}/api/auth/login`, {
      usuario,
      clave
    });

    // El backend puede devolver 'user' o 'usuario'
    const usuarioData = data.user || data.usuario;

    if (!usuarioData) {
      throw new Error('Respuesta de servidor inválida: no se recibió información de usuario.');
    }

    // ⛔ Validar estatus
    if (!usuarioData.estatus) {
      throw new Error('Este usuario no tiene acceso a este sistema.');
    }

    // ⛔ Validar rol
    if (usuarioData.rol !== 'AUDITORIA') {
      throw new Error('Este usuario no tiene acceso a este sistema.');
    }

    // Si pasa las validaciones, seguimos
    const expires_at = Date.now() + data.expires_in * 1000;

    const payload = {
      token: data.access_token,
      token_type: data.token_type,
      expires_at,
      user: usuarioData
    };

    localStorage.setItem(TOKEN_KEY, JSON.stringify(payload));
    setTokenInfo(payload);
    setUser(payload.user);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setTokenInfo(null);
    setUser(null);
  };

  const isAuthenticated = !!tokenInfo && tokenInfo.expires_at > Date.now();

  // 👉 función para usar en cualquier llamada axios
  const getAuthHeader = () => {
    if (!tokenInfo || !tokenInfo.token) return {};
    return {
      Authorization: `${tokenInfo.token_type || 'Bearer'} ${tokenInfo.token}`
    };
  };

  // 🔔 Manejar expiración del token con SweetAlert
  useEffect(() => {
    if (!tokenInfo) return;

    const msLeft = tokenInfo.expires_at - Date.now();

    // Función que se ejecuta cuando el token expira
    const handleTokenExpired = () => {
      // Evitar que se dispare si ya no hay token
      if (!tokenInfo) return;

      Swal.fire({
        icon: 'warning',
        title: 'Sesión expirada',
        text: 'Tu tiempo ha expirado, por favor inicia sesión de nuevo.',
        confirmButtonText: 'Aceptar'
      }).then(() => {
        logout();
      });
    };

    if (msLeft <= 0) {
      handleTokenExpired();
    } else {
      const id = setTimeout(handleTokenExpired, msLeft);
      return () => clearTimeout(id);
    }
  }, [tokenInfo]); // se re-calcula cada vez que haces login / refresh

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        login,
        logout,
        loading,
        tokenInfo,
        getAuthHeader
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
